import { UiNode } from '@ory/client';
import { Divider, Paper, PasswordInput, Stack, Text } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { IconCheck } from '@tabler/icons-react';
import { Button, TextInput } from 'src/components';
import { AuthFeedback } from 'src/auth/components/AuthFeedback';
import {
  PasswordRequirements,
  passwordSchema,
  allPasswordRequirementsMet,
  PasswordFormValues,
} from 'src/components/convenience/auth/PasswordRequirements';
import { OidcSection } from 'src/components/convenience/auth/OidcSection';
import { TermsAndPrivacy } from 'src/auth/components/TermsAndPrivacy';

interface InviteRegistrationFormProps {
  userEmail: string;
  error: string | null;
  isSubmitting: boolean;
  onSubmit: (password: string) => void;
  oidcNodes: UiNode[];
  formAction: string;
  csrfToken: string;
}

export const InviteRegistrationForm = ({
  userEmail,
  error,
  isSubmitting,
  onSubmit,
  oidcNodes,
  formAction,
  csrfToken,
}: InviteRegistrationFormProps) => {
  const form = useForm<PasswordFormValues>({
    mode: 'controlled',
    initialValues: {
      password: '',
    },
    validate: zodResolver(passwordSchema),
    validateInputOnChange: true,
  });

  const password = form.values.password;
  const allRequirementsMet = allPasswordRequirementsMet(password);

  const handleSubmit = (values: PasswordFormValues) => {
    onSubmit(values.password);
  };

  return (
    <Paper bg="gray.0" radius="xl" py={48} px={48} w={450}>
      <Stack gap="md">
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            <Text fz="xl" fw={600} ta="center">
              Enter a strong password
              <br />
              to create an account
            </Text>

            <TextInput
              label="Email"
              value={userEmail}
              disabled
              withAsterisk
              rightSection={
                userEmail ? <IconCheck size={16} color="var(--mantine-color-green-6)" /> : null
              }
            />

            <PasswordInput
              label="Create password"
              placeholder="Enter your password"
              withAsterisk
              autoFocus
              key={form.key('password')}
              {...form.getInputProps('password')}
              error={false}
            />

            <PasswordRequirements password={password} />

            <AuthFeedback error={error} />

            <Button
              type="submit"
              fullWidth
              loading={isSubmitting}
              disabled={!allRequirementsMet || isSubmitting}
            >
              Create account
            </Button>
          </Stack>
        </form>

        <Divider label="or" labelPosition="center" />

        <OidcSection
          nodes={oidcNodes}
          formAction={formAction}
          csrfToken={csrfToken}
          action="link"
        />

        <TermsAndPrivacy />
      </Stack>
    </Paper>
  );
};
